import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ConnectionAuthKind } from 'shell-models-lib';
import { parameterizeDynamicsControlMetadata } from '../consts/metadata-consts';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

const connectionForm: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'server',
        label: stringResources.ModelName_ServerName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox-number',
        bind: 'port',
        label: stringResources.ModelName_Port,
        isLabelVertical: true,
        inputType: 'number',
        min: 0,
        defaultValue: 5433
    },
    {
        type: 'textbox',
        bind: 'databaseName',
        label: stringResources.ModelName_DatabaseName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'userName',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {

        type: 'dynamic-akv-switcher',
        hideAKVSwitcherInConnStrProps: true,
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            isLabelVertical: true
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Vertica_DisplayText),
            stringResources.ModelName_Password)
    }
];

const additionalFields: ControlMetadata = {
    type: 'key-val-list',
    bind: 'additionalFields',
    label: stringResources.AdditionalConnectionStringFields
};

function selectSwitch(supportParam: boolean): ControlMetadata {
    const akvMetadata: ControlMetadata = {
        type: 'azure-key-vault',
        bind: 'referedAKV',
        isLabelVertical: true
    };

    const defaultForm = supportParam ? connectionForm.map(parameterizeDynamicsControlMetadata) as ControlMetadata[] : connectionForm;

    return {
        type: 'select-button-switch',
        required: true,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.ModelName_ConnectionString,
                value: false
            },
            {
                label: stringResources.AzureKeyVault_DisplayText,
                value: true,
                hideAKVOptionIfNotChosen: false
            }
        ],
        bind: 'enableAKV',
        isNotRelatedToCredential: true,
        defaultValue: false,
        cases: [
            {
                caseValue: true,
                children: [
                    supportParam ? {
                        type: 'parameterizable',
                        attribute: 'referedAKV',
                        child: akvMetadata,
                        disableAddParameter: true
                    } : akvMetadata
                ]
            },
            {
                caseValue: false,
                children: [
                    ...defaultForm,
                    additionalFields
                ]
            }
        ]
    };
}

export const verticaMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.vertica.linkedServiceType,
    displayName: stringResources.Vertica_DisplayText,
    supportedInTrident: true,
    supportAutoFillCopilotInput: true,
    isKnownInPipelineContext: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: ResourceKind.Vertica
    },
    supportedAuthKindsInTrident: [
        ConnectionAuthKind.Basic
    ],
    supportOPDG: GlobalVariables.enableOPDG,
    supportCopyJobSource: true,
    supportMultipleIncrementalColumns: true,
    opdgOnly: true,
    isOnPremises: true,// Please note legacy one still supports Azure IR. We have a special handling in LS config component
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-vertica'
    },
    isDeprecated: !GlobalVariables.showLegacy ? '{ version !== "v2" }' : false,
    showWarningMessage: '{ version !== "v2" }',
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase()
    ),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-vertica',
    isFirstClass: true,
    category: ['Database'],
    supportDatasetV2: true,
    SHIRVersion: {
        v2: '5.44.8984.1'
    },
    copySinkTypes: [],
    copySourceTypes: [adfModelTypeMap.vertica.copySourceType],
    supportFirstClassParameterExperience: true,
    controls: [
        {
            type: 'radio-switch',
            bind: 'version',
            label: stringResources.VersionLabel,
            isLabelVertical: true,
            required: true,
            options: driverVersionVersionOptions,
            defaultValue: driverVersions.v2,
            hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
            cases: [
                {
                    caseValue: driverVersions.v1,
                    children: [
                        {
                            type: 'hint',
                            hintMode: 'warning',
                            description: format(
                                stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                stringResources.VersionLabel.toLocaleLowerCase()),
                            link: 'https://learn.microsoft.com/azure/data-factory/connector-vertica'
                        },
                        selectSwitch(true)
                    ]
                },
                {
                    caseValue: driverVersions.v2,
                    children: [
                        ...connectionForm.map(parameterizeDynamicsControlMetadata),
                        additionalFields
                    ]
                }
            ]
        },
        ...(connectionForm
                .map(parameterizeDynamicsControlMetadata)
                .map((control) => ({
                    ...control,
                    hide: !GlobalVariables.showLegacy ? '{version !== "v2" }' : true
                }))
            )
    ]
};
